import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { ArrowRight, Home as HomeIcon, DollarSign, TrendingUp, Users, Calendar, Phone, Building2, Wrench, FileText, Shield, MessageSquare, Search, Gavel, Brain, BadgeCheck, Briefcase } from 'lucide-react';

const HERO_BG = "https://images.unsplash.com/photo-1578439297699-eb414262c2de?w=1920&q=80";
const SECONDARY_BG = "https://images.unsplash.com/photo-1548199569-73993001d014?w=1920&q=80";
const BROKER_PHOTO = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/c90cntlv_fc0fb51c619dc3497bc8ab60234171d3da217eab-4.jpeg";
const QR_CODE = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/g44ovk6k_attachment_3.jpeg";

const funnels = [
  { icon: HomeIcon, title: 'Buy a Home', description: 'Search properties and get pre-approved for financing.', link: '/buyers', cta: 'Find Your Home', color: '#3B82F6' },
  { icon: DollarSign, title: 'Sell Your Home', description: 'Free home valuation and expert marketing to get top dollar.', link: '/sellers', cta: "What's My Home Worth?", color: '#22C55E' },
  { icon: TrendingUp, title: 'Invest', description: 'Off-market deals and investor financing. Build your portfolio.', link: '/investors', cta: 'Get Off-Market Deals', color: '#D4AF37' },
  { icon: Users, title: 'Become an Agent', description: 'Join a growth-focused brokerage. Training + leads provided.', link: '/join', cta: 'Apply Now', color: '#EC4899' }
];

const brands = [
  { abbr: 'SRI', name: 'Simple Realty & Investments\u2122', desc: 'Buying, Selling & Investing', icon: HomeIcon, link: '/buyers' },
  { abbr: 'SMF', name: 'Simple Mortgage Financing', desc: 'FHA, VA, Conventional, DSCR', icon: DollarSign, link: '/financing' },
  { abbr: 'SRIM', name: 'Simple Realty Investment Mgmt', desc: 'Full-Service Property Management', icon: Building2, link: '/property-management' },
  { abbr: 'CCS', name: 'Certified Closing Services', desc: 'Notary & Closing Services', icon: FileText, link: '/notary' },
  { abbr: 'MMF', name: 'Make My Fix', desc: 'Repairs & Maintenance', icon: Wrench, link: '/contact' },
];

const stats = [
  { number: '500+', label: 'Properties Sold' },
  { number: '$150M+', label: 'Total Sales Volume' },
  { number: '98%', label: 'Client Satisfaction' },
  { number: '15+', label: 'Years Experience' }
];

const authorityTraits = [
  { icon: Gavel, title: 'Deal Negotiator', description: 'I fight for the best terms. Every contract, every clause, every dollar — negotiated to your advantage.' },
  { icon: Brain, title: 'Problem Solver', description: 'Title issues, inspection surprises, financing hiccups — I find solutions when others walk away.' },
  { icon: BadgeCheck, title: 'Former Dept. of Corrections', description: 'Discipline, structure, and accountability. I bring the same focus to closing your real estate deal.' },
  { icon: Briefcase, title: 'Case Manager', description: 'Complex situations are my specialty. I manage every detail so nothing falls through the cracks.' },
];

export const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section min-h-screen"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="hero-section"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-[#D4AF37] mb-1 opacity-0 animate-fade-in-up tracking-wide" style={{ fontFamily: 'Playfair Display' }} data-testid="hero-company-name">
            Simple Realty &amp; Investments&trade;
          </p>
          <p className="text-sm sm:text-base text-[#C0C0C0] mb-8 opacity-0 animate-fade-in-up animation-delay-50 tracking-wider uppercase" style={{ fontFamily: 'Outfit', letterSpacing: '0.15em' }} data-testid="hero-tagline">
            Real Estate Brokerage, Investment &amp; Property Management Services
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#F5F5F0] mb-4 opacity-0 animate-fade-in-up animation-delay-100 tracking-tight leading-tight" style={{ fontFamily: 'Playfair Display' }} data-testid="hero-headline">
            I Solve Real Estate Problems.{' '}
            <span className="text-gold-gradient">I Get Deals Closed.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#C0C0C0] mb-8 opacity-0 animate-fade-in-up animation-delay-150" style={{ fontFamily: 'Outfit' }} data-testid="hero-subheadline">
            Broker | Deal Negotiator | Problem Solver — Serving South Florida
          </p>
          
          {/* 3 Primary CTAs */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-2xl mx-auto mb-6 opacity-0 animate-fade-in-up animation-delay-200">
            <Link to="/buyers" className="flex-1">
              <Button className="btn-gold w-full py-5 text-base font-semibold" data-testid="hero-search-homes-btn">
                <Search className="w-5 h-5 mr-2" /> Search Homes
              </Button>
            </Link>
            <Link to="/sellers" className="flex-1">
              <Button className="btn-gold w-full py-5 text-base font-semibold" data-testid="hero-sell-property-btn">
                <DollarSign className="w-5 h-5 mr-2" /> Sell Your Property
              </Button>
            </Link>
            <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer" className="flex-1">
              <Button className="btn-gold w-full py-5 text-base font-semibold" data-testid="hero-book-btn">
                <Calendar className="w-5 h-5 mr-2" /> Book Appointment
              </Button>
            </a>
          </div>

          {/* Secondary actions */}
          <div className="flex justify-center gap-3 opacity-0 animate-fade-in-up animation-delay-250">
            <a href="tel:7863560878">
              <Button variant="outline" className="btn-outline-gold text-sm py-2.5" data-testid="hero-call-btn">
                <Phone className="w-4 h-4 mr-1" /> (786) 356-0878
              </Button>
            </a>
            <a href="sms:7863560878">
              <Button variant="outline" className="btn-outline-gold text-sm py-2.5" data-testid="hero-text-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> Text Us
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 opacity-0 animate-fade-in-up animation-delay-300">
            {stats.map((stat) => (
              <div key={stat.label} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="py-20 bg-[#111111]" data-testid="authority-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Why Clients Trust Me</p>
            <h2 className="text-3xl sm:text-4xl font-light text-[#F5F5F0] mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>
              I Handle <span className="text-gold-gradient">Complex Deals</span> & Get Them Closed
            </h2>
            <p className="text-[#808080] max-w-2xl mx-auto">
              Most agents list homes. I solve problems. From tough negotiations to impossible deadlines, I bring structure, discipline, and results to every transaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {authorityTraits.map((trait) => (
              <div key={trait.title} className="bg-[#0a0a0a] border border-[#222] p-6 rounded-xl hover:border-[#D4AF37]/40 transition-all" data-testid={`authority-${trait.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                  <trait.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-medium text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  {trait.title}
                </h3>
                <p className="text-[#808080] text-sm leading-relaxed">{trait.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Home Valuation - Lead Capture */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="home-valuation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-2xl p-8 sm:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="section-overline">Thinking of Selling?</p>
                <h2 className="text-3xl sm:text-4xl font-light text-[#F5F5F0] mt-2 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  What's Your Home <span className="text-gold-gradient">Worth?</span>
                </h2>
                <p className="text-[#808080] leading-relaxed mb-6">
                  Get a free, no-obligation home valuation from a local expert. I'll analyze recent sales, market trends, and your home's unique features to give you an accurate price.
                </p>
                <ul className="space-y-2 text-[#F5F5F0] mb-6">
                  {['Free market analysis', 'No obligation', 'Results in 24 hours', 'Expert pricing strategy'].map(item => (
                    <li key={item} className="flex items-center text-sm"><div className="w-2 h-2 rounded-full bg-[#D4AF37] mr-3" />{item}</li>
                  ))}
                </ul>
                <a href="tel:7863560878">
                  <Button variant="outline" className="btn-outline-gold">
                    <Phone className="w-4 h-4 mr-2" /> Or Call (786) 356-0878
                  </Button>
                </a>
              </div>
              <LeadForm
                source="home_valuation"
                title="Get Your Free Valuation"
                subtitle="Enter your details and property address."
                buttonText="Get My Home Value"
                showPropertyInterest={true}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Funnel Cards */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="funnel-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">How Can I Help?</p>
            <h2 className="text-3xl sm:text-4xl font-light text-[#F5F5F0] mt-2" style={{ fontFamily: 'Playfair Display' }}>
              Choose Your <span className="text-gold-gradient">Path</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {funnels.map((funnel) => (
              <Link 
                key={funnel.title} 
                to={funnel.link}
                className="bg-[#111111] border border-[#222] p-6 rounded-xl hover:border-[#D4AF37]/50 transition-all group"
                data-testid={`funnel-card-${funnel.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${funnel.color}15` }}>
                  <funnel.icon className="w-6 h-6" style={{ color: funnel.color }} />
                </div>
                <h3 className="text-lg font-medium text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>{funnel.title}</h3>
                <p className="text-[#808080] text-sm mb-4">{funnel.description}</p>
                <span className="text-sm font-semibold flex items-center group-hover:translate-x-1 transition-transform" style={{ color: funnel.color }}>
                  {funnel.cta} <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Ecosystem */}
      <section className="py-20 bg-[#111111]" data-testid="brand-ecosystem">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Our Family of Services</p>
            <h2 className="text-3xl sm:text-4xl font-light text-[#F5F5F0] mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>
              One Call <span className="text-gold-gradient">Handles It All</span>
            </h2>
            <p className="text-[#808080] max-w-xl mx-auto">
              From finding your dream home to financing, managing, and maintaining it — we've got every step covered.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {brands.map((brand) => (
              <Link key={brand.abbr} to={brand.link} className="bg-[#0a0a0a] border border-[#222] p-5 rounded-xl hover:border-[#D4AF37]/40 transition-all text-center group" data-testid={`brand-${brand.abbr.toLowerCase()}`}>
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                  <brand.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h4 className="text-[#D4AF37] text-lg font-bold mb-1">{brand.abbr}</h4>
                <p className="text-[#F5F5F0] text-xs font-medium mb-1">{brand.name}</p>
                <p className="text-[#808080] text-[11px]">{brand.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="tel:7863560878">
              <Button className="btn-gold px-10 py-5 text-base font-semibold" data-testid="brand-cta-call">
                <Phone className="w-5 h-5 mr-2" /> Call (786) 356-0878
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Broker Section */}
      <section className="py-24 relative" style={{ backgroundImage: `url(${SECONDARY_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} data-testid="about-section">
        <div className="absolute inset-0 bg-[#0a0a0a]/90" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Meet Your Broker</p>
              <h2 className="section-title mt-2 mb-6">Sean McClain</h2>
              <p className="text-[#808080] leading-relaxed mb-6">
                As the Broker/Owner of Simple Realty &amp; Investments&trade;, I bring years of experience and a passion for helping clients achieve their real estate goals. Whether you're a first-time buyer, seasoned investor, or looking to sell, I'm here to make the process simple and stress-free.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:7863560878"><Button className="btn-gold" data-testid="broker-call-btn"><Phone className="w-4 h-4 mr-2" /> Call Sean</Button></a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer"><Button variant="outline" className="btn-outline-gold" data-testid="broker-calendly-btn"><Calendar className="w-4 h-4 mr-2" /> Book a Call</Button></a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img src={BROKER_PHOTO} alt="Sean McClain - Broker" className="w-80 h-80 object-cover rounded-lg border-2 border-[#D4AF37] gold-glow" />
                <div className="absolute -bottom-6 -right-6 p-4 bg-[#111111] border border-[#D4AF37]/20 rounded-lg">
                  <p className="text-[#D4AF37] font-semibold text-sm">Broker / Owner</p>
                  <p className="text-[#F5F5F0] text-xs">Licensed FL Broker</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QR Code */}
      <section className="py-12 bg-[#111111]" data-testid="qr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <img src={QR_CODE} alt="QR Code" className="w-40 h-40 rounded-lg mx-auto" />
          <p className="text-[#808080] text-sm mt-3">Scan to connect with us instantly</p>
        </div>
      </section>

      {/* Lead Capture / Contact */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="lead-capture-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="section-overline">Get Started Today</p>
              <h2 className="text-3xl sm:text-4xl font-light text-[#F5F5F0] mt-2 mb-6" style={{ fontFamily: 'Playfair Display' }}>
                Ready to Make <span className="text-gold-gradient">Your Move</span>?
              </h2>
              <p className="text-[#808080] leading-relaxed mb-8">
                Fill out the form and I'll personally reach out within 24 hours. Or take the fast track:
              </p>
              <div className="space-y-3 mb-8">
                <a href="tel:7863560878" className="flex items-center text-[#F5F5F0] hover:text-[#D4AF37] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4"><Phone className="w-5 h-5 text-[#D4AF37]" /></div>
                  <span className="font-semibold">(786) 356-0878</span>
                </a>
                <a href="sms:7863560878" className="flex items-center text-[#F5F5F0] hover:text-[#D4AF37] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4"><MessageSquare className="w-5 h-5 text-[#D4AF37]" /></div>
                  <span>Text us anytime</span>
                </a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#F5F5F0] hover:text-[#D4AF37] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4"><Calendar className="w-5 h-5 text-[#D4AF37]" /></div>
                  <span>Book a free consultation</span>
                </a>
              </div>
            </div>
            <LeadForm source="home" title="Contact Us" subtitle="Tell us how we can help and we'll get back to you fast." buttonText="Send Message" showPropertyInterest={true} />
          </div>
        </div>
      </section>
    </div>
  );
};
